import { useTranslation } from "react-i18next";
import {
  ExternalLink as ExternalLinkIcon,
  Info as InfoIcon,
  Send as SendIcon,
  MessageSquare as MessageSquareIcon,
  Phone as PhoneIcon,
  PhoneCall as PhoneCallIcon,
} from "lucide-react";
import { MixedLocaleValue, TechnicalValue } from "../../TechnicalValue.js";
import type { TemplateListDetailsButton } from "../../template-list-presentation.js";
import {
  buildFormTemplatePresentation,
  type TemplateFormState,
} from "../template-management-form.js";
import type {
  TemplateCreationCategory,
  TemplateCreationUseCase,
} from "../template-creation-wizard-state.js";
import { buildTemplateReviewModel } from "./template-creation-review.js";
import { TemplateEditorPreviewPanel } from "./TemplateEditorPreviewPanel.js";

export interface TemplateReviewStepProps {
  category: TemplateCreationCategory;
  useCase: TemplateCreationUseCase;
  formState: TemplateFormState;
}

function contentBlockTitle(
  kind: "header" | "body" | "footer" | "other",
  t: (key: string) => string,
): string {
  switch (kind) {
    case "header":
      return t("whatsapp.templates.list.detailPanel.contentHeader");
    case "body":
      return t("whatsapp.templates.list.detailPanel.contentBody");
    case "footer":
      return t("whatsapp.templates.list.detailPanel.contentFooter");
    case "other":
      return t("whatsapp.templates.list.detailPanel.contentOther");
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

function ButtonTypeIcon({
  kind,
}: {
  kind: TemplateListDetailsButton["kind"];
}): React.JSX.Element {
  switch (kind) {
    case "URL":
      return <ExternalLinkIcon size={14} aria-hidden="true" />;
    case "PHONE_NUMBER":
      return <PhoneIcon size={14} aria-hidden="true" />;
    case "VOICE_CALL":
      return <PhoneCallIcon size={14} aria-hidden="true" />;
    case "QUICK_REPLY":
    case "OTHER":
      return <MessageSquareIcon size={14} aria-hidden="true" />;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function TemplateReviewStep({
  category,
  useCase,
  formState,
}: TemplateReviewStepProps): React.JSX.Element {
  const { t } = useTranslation();
  const review = buildTemplateReviewModel({ formState, category, useCase });
  // Same Step-2 presentation source — live form state, no snapshot.
  const presentation = buildFormTemplatePresentation(formState);
  const previewPanel = (
    <TemplateEditorPreviewPanel
      presentation={presentation}
      contentLanguage={formState.language}
    />
  );

  const sectionHeadingClass =
    "m-0 text-[0.75rem] font-semibold text-ink-muted";

  return (
    <div className="mx-auto grid w-full max-w-5xl grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(18.5rem,24rem)] lg:gap-6">
      <div className="grid min-w-0 content-start gap-4">
        <div className="flex items-start gap-3 text-start">
          <span
            className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent"
            aria-hidden="true"
          >
            <SendIcon size={18} />
          </span>
          <div className="grid gap-1">
            <h2 className="m-0 text-lg font-semibold tracking-[-0.02em] text-ink">
              {t("whatsapp.templates.manage.wizard.review.title")}
            </h2>
            <p className="m-0 text-sm leading-relaxed text-ink-muted">
              {t("whatsapp.templates.manage.wizard.review.description")}
            </p>
          </div>
        </div>

        <section className="grid gap-3 rounded-xl border border-line bg-surface p-4 shadow-sm sm:p-5">
          <h3 className={sectionHeadingClass}>
            {t("whatsapp.templates.manage.wizard.review.summaryHeading")}
          </h3>
          <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <div className="grid min-w-0 gap-0.5">
              <dt className="m-0 text-[0.6875rem] text-ink-subtle">
                {t("whatsapp.templates.manage.fields.name")}
              </dt>
              <dd className="m-0 truncate text-sm font-semibold text-ink">
                <TechnicalValue>{review.name}</TechnicalValue>
              </dd>
            </div>
            <div className="grid min-w-0 gap-0.5">
              <dt className="m-0 text-[0.6875rem] text-ink-subtle">
                {t("whatsapp.templates.manage.language")}
              </dt>
              <dd className="m-0 text-sm font-medium text-ink">
                <span dir="ltr" className="[unicode-bidi:isolate]">
                  {review.languageLabel}
                </span>
              </dd>
            </div>
            <div className="grid min-w-0 gap-0.5">
              <dt className="m-0 text-[0.6875rem] text-ink-subtle">
                {t("whatsapp.templates.manage.fields.category")}
              </dt>
              <dd className="m-0 text-sm font-medium text-ink">
                {`${t(review.categoryLabelKey)} · ${t(review.useCaseTitleKey)}`}
              </dd>
            </div>
            <div className="grid min-w-0 gap-0.5">
              <dt className="m-0 text-[0.6875rem] text-ink-subtle">
                {t(review.variableTypeFieldKey)}
              </dt>
              <dd className="m-0 text-sm font-medium text-ink">
                {t(review.variableTypeValueKey)}
              </dd>
            </div>
          </dl>
        </section>

        {review.contentBlocks.length > 0 || review.buttons.length > 0 ? (
          <section className="grid gap-3 rounded-xl border border-line bg-surface p-4 shadow-sm sm:p-5">
            <h3 className={sectionHeadingClass}>
              {t("whatsapp.templates.manage.wizard.review.contentHeading")}
            </h3>
            {review.contentBlocks.length > 0 ? (
              <dl className="m-0 grid divide-y divide-line overflow-hidden rounded-lg border border-line">
                {review.contentBlocks.map((block) => (
                  <div
                    key={block.key}
                    className="grid gap-1 px-3 py-2.5 sm:grid-cols-[6.5rem_minmax(0,1fr)] sm:gap-3"
                  >
                    <dt className="m-0 text-[0.75rem] font-medium text-ink-muted">
                      {contentBlockTitle(block.kind, t)}
                    </dt>
                    <dd className="m-0 min-w-0">
                      <MixedLocaleValue className="wrap-anywhere whitespace-pre-wrap text-[0.8125rem] leading-snug text-ink">
                        {block.text}
                      </MixedLocaleValue>
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}

            {review.buttons.length > 0 ? (
              <div className="grid gap-2">
                <h4 className="m-0 text-[0.75rem] font-medium text-ink-muted">
                  {t("whatsapp.templates.manage.wizard.review.buttonsHeading")}
                </h4>
                <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
                  {review.buttons.map((button) => (
                    <li
                      key={button.key}
                      className="flex min-w-0 max-w-full items-center gap-2 rounded-lg border border-line bg-surface-muted/60 px-2.5 py-1.5"
                    >
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-surface text-ink-muted">
                        <ButtonTypeIcon kind={button.kind} />
                      </span>
                      <span className="grid min-w-0">
                        <MixedLocaleValue className="truncate text-[0.8125rem] font-medium text-ink">
                          {button.label}
                        </MixedLocaleValue>
                        <span className="truncate text-[0.6875rem] text-ink-muted">
                          {button.secondary
                            ? button.secondaryKind === "ttl"
                              ? t("whatsapp.templates.list.detailPanel.voiceActiveDays", {
                                  days: button.secondary,
                                })
                              : (
                                <TechnicalValue>{button.secondary}</TechnicalValue>
                              )
                            : t(button.typeLabelKey)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>
        ) : null}

        {review.examples.length > 0 ? (
          <section className="grid gap-2 rounded-xl border border-line bg-surface p-4 shadow-sm sm:p-5">
            <h3 className={sectionHeadingClass}>
              {t("whatsapp.templates.manage.wizard.review.examplesHeading")}
            </h3>
            <dl className="m-0 grid gap-2 sm:grid-cols-2">
              {review.examples.map((example) => (
                <div
                  key={`${example.scope}-${example.token}`}
                  className="grid min-w-0 gap-0.5 rounded-md bg-surface-muted/60 px-3 py-2"
                >
                  <dt className="m-0 text-[0.6875rem] text-ink-muted">
                    {example.scope === "header"
                      ? t("whatsapp.templates.manage.wizard.editor.variableGroupHeader")
                      : t("whatsapp.templates.list.detailPanel.contentBody")}{" "}
                    <TechnicalValue className="text-[0.6875rem]">{example.token}</TechnicalValue>
                  </dt>
                  <dd className="m-0 min-w-0">
                    <MixedLocaleValue className="wrap-anywhere text-[0.8125rem] text-ink">
                      {example.value}
                    </MixedLocaleValue>
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        <div
          className="flex items-start gap-2.5 rounded-xl border border-accent/25 bg-accent-soft px-4 py-3 text-start"
          role="note"
        >
          <InfoIcon size={16} className="mt-0.5 shrink-0 text-accent" aria-hidden="true" />
          <p className="m-0 text-[0.8125rem] leading-relaxed text-ink">
            {t("whatsapp.templates.manage.wizard.review.notice")}
          </p>
        </div>

        <div className="lg:hidden">{previewPanel}</div>
      </div>

      <aside className="hidden lg:block sticky top-0 self-start">{previewPanel}</aside>
    </div>
  );
}
